import Link from 'next/link';
import React from 'react';
import { Echo, Foxtrot } from '@music-vine/cadence/ui';
import { cn } from '@/lib/utils';
import { PopupMedia } from './PopupMedia';
import type { ReactElement, ReactNode } from 'react';
import type { PopUpVariant } from './PagePopup';
interface PopupContentProps {
  children: ReactNode;
  className?: string;
  href?: string;
  icon?: ReactNode;
  mainTitle?: ReactNode;
  onHrefClick?: () => void;
  subtitle?: string;
  variant?: PopUpVariant;
}
const hasContent = (content: string | ReactNode | undefined): boolean => {
  if (content === undefined || content === null) {
    return false;
  }
  if (typeof content === 'string') {
    return content.length > 0;
  }
  return true;
};

// Type guard for PopupMedia components
const isPopupMediaElement = (element: ReactNode): element is ReactElement => React.isValidElement(element) && element.type === PopupMedia;

// Extract media and non-media content without React.Children methods
const extractContent = (children: ReactNode) => {
  // If children is a single element, handle it directly
  if (isPopupMediaElement(children)) {
    return {
      mediaContent: children,
      otherContent: null
    };
  }

  // If children is an array, process it
  if (Array.isArray(children)) {
    const childrenArray = children as ReactNode[];
    const mediaContent = childrenArray.find(isPopupMediaElement);
    const otherContent = childrenArray.filter(child => !isPopupMediaElement(child));
    return {
      mediaContent,
      otherContent
    };
  }

  // For all other cases, return everything as other content
  return {
    mediaContent: null,
    otherContent: children
  };
};
export const PopupContent = ({
  subtitle,
  mainTitle,
  children,
  className,
  variant = 'default',
  href,
  icon,
  onHrefClick
}: PopupContentProps) => {
  const {
    mediaContent,
    otherContent
  } = extractContent(children);
  if (variant === 'banner-img-pu') {
    return <div className="flex h-full flex-col">
        {mediaContent}
        <div className="grow space-y-4 p-5 px-8">
          {hasContent(subtitle) && <div className="font-sans text-pink-500 text-xs">{subtitle}</div>}
          {hasContent(mainTitle) && <Foxtrot>{mainTitle}</Foxtrot>}
          {otherContent}
        </div>
      </div>;
  }
  if (variant === 'mobile-banner-image') {
    return <Link href={href ?? ''} onClick={onHrefClick} target="_blank">
        <div className="flex flex-col">
          <div className="flex h-24 flex-row items-center">
            <div className="w-[60%] px-3">
              {hasContent(subtitle) && <div className="mb-0.5 truncate font-sans text-pink-600 text-xs dark:text-white">
                  {subtitle}
                </div>}
              {hasContent(mainTitle) && <div className="line-clamp-2 text-ellipsis font-bold font-heading text-gray-950 text-lg leading-tight dark:text-white">
                  {mainTitle}
                </div>}
            </div>
            <div className="h-full w-[40%]">{mediaContent}</div>
          </div>
          {otherContent}
        </div>
      </Link>;
  }
  if (variant === 'mobile-banner-dialog') {
    return <div className="flex flex-col items-center gap-4 px-4 pt-3 pb-4">
        <div className="inline-block text-center leading-tight">
          {hasContent(subtitle) && <span className="font-bold font-sans text-xs dark:text-white">
              {subtitle}
            </span>}
          {hasContent(mainTitle) && <span className="font-sans text-gray-950 text-xs dark:text-white">
              {mainTitle}
            </span>}
        </div>
        {otherContent}
      </div>;
  }

  // This new variant is best used when making a mobile banner that uses other components, it does not support having a top banner image. Images can still be used and would be rendered below the preset text content. Use when you want to keep the main styles the same as those on the defualt variant on desktop, but do not want it to be rendered within a drawer.
  if (variant === 'mobile-banner-default') {
    return <div className={cn('w-full bg-white px-4 pt-5 pb-6 dark:bg-gray-950', {})}>
        <div className="flex flex-col gap-3">
          <div className={cn('flex flex-col gap-2 text-center md:text-left', {})}>
            {hasContent(subtitle) && <div className="font-sans text-pink-600 text-sm dark:text-white">
                {subtitle}
              </div>}
            {hasContent(mainTitle) && <Echo className="-mb-1">{mainTitle}</Echo>}
          </div>
          {otherContent}
        </div>
      </div>;
  }
  if (variant === 'mobile-payment-issue') {
    return <div className="flex flex-col items-center justify-center gap-2 px-4 pt-5 pb-6">
        <div className="text-center font-heading font-semibold text-lg dark:text-white">
          {mainTitle}
        </div>
        <div className="w-full font-sans text-gray-700 text-sm dark:text-gray-200">
          {otherContent}
        </div>
      </div>;
  }
  return <div className={cn('flex h-full w-full flex-col gap-2', className)} data-sentry-component="PopupContent" data-sentry-source-file="PopupContent.tsx">
      <div className="flex flex-col gap-1">
        {hasContent(icon) && <>{icon}</>}
        {hasContent(subtitle) && <div className="font-sans text-pink-600 text-sm dark:text-white">
            {subtitle}
          </div>}
        {hasContent(mainTitle) && <div className="font-heading font-semibold text-gray-950 text-lg tracking-tighter dark:text-white">
            {mainTitle}
          </div>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>;
};
export const PopupActions = ({
  children
}: {
  children: ReactNode;
}) => <div className="mt-8 flex items-center justify-end gap-3" data-sentry-component="PopupActions" data-sentry-source-file="PopupContent.tsx">{children}</div>;